import React, { useCallback, useEffect, type PropsWithChildren, type ReactNode } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import { QuickstartNudge } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/quickstart-nudge/main.tsx';
import { JWM_FEEDBACK_TASK } from '@atlassian/jira-onboarding-quickstart-core/src/common/constants';
import {
	useIsDismissed,
	useOpenTask,
	useQuickstartActions,
} from '@atlassian/jira-onboarding-quickstart-core/src/services';
import { FeedbackNudgeAsync } from '@atlassian/jira-software-onboarding-nudges--next/src/ui/jwm-feedback/async.tsx';
import { useHasOpened } from '@atlassian/jira-software-onboarding-quickstart-has-opened/src/services/has-opened/index.tsx';
import {
	useSideNavVisibility,
	useExpandSideNav,
} from '@atlassian/navigation-system/layout/side-nav';

export function SpotlightableFeedbackButton({ children }: PropsWithChildren) {
	const renderNudgeWrapper = useCallback(
		(kids: ReactNode) => <FeedbackNudgeAsync>{kids}</FeedbackNudgeAsync>,
		[],
	);
	const [{ hasOpened: shouldShowNudge }] = useHasOpened();
	const [isUnloaded] = useIsDismissed();
	const { isSideNavVisible, showSideNav } = useSideNavVisibility();
	const expandSideNav = useExpandSideNav();
	const [openTask] = useOpenTask();
	const [, { setHasNudgeActive }] = useQuickstartActions();

	useEffect(() => {
		if (fg('jira_nav4_remove_internal_sidenav_state_hook')) {
			if (!isUnloaded && openTask === JWM_FEEDBACK_TASK && shouldShowNudge) {
				expandSideNav();
				setHasNudgeActive(true); // This re-shows the button-highlight, which can be hidden if the user tries to hide the sidebar
			}

			return;
		}

		// Check if the sidebar is visible, and open it if it isn't open
		if (!isUnloaded && openTask === JWM_FEEDBACK_TASK && shouldShowNudge && !isSideNavVisible) {
			showSideNav();
			setHasNudgeActive(true); // This re-shows the button-highlight, which can be hidden if the user tries to hide the sidebar
		}
	}, [
		isUnloaded,
		openTask,
		shouldShowNudge,
		setHasNudgeActive,
		expandSideNav,
		isSideNavVisible,
		showSideNav,
	]);

	return !__SERVER__ && shouldShowNudge ? (
		<QuickstartNudge renderWrapper={renderNudgeWrapper}>{children}</QuickstartNudge>
	) : (
		children
	);
}
